/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useCallback, useEffect, useMemo, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { ImpoundPolicyListResponse, InsCompResponse, MiscPayeeInfoResponse } from '../../common/queryFunctions';
import { epochToDate, formatDate, isAlphanumeric, phoneNumberFormatter } from '../../common/UtilityFunctions';
import { useAuthContext } from '../../contexts/AuthContext';
import css from '../../css/loan-escrow-accruals-styles/HazardAccruals.module.css';
import useInsCompInfo from '../../common/hooks/useInsCompInfo';
import useMiscPayeeInfo from '../../common/hooks/useMiscPayeeInfo';

interface ServicingArrayEntryImpound extends ImpoundPolicyListResponse {
  payAgent: boolean;
  unitNo: string;
  agentCompany: string;
  servArrComment: string;
  policyNo: string;
  servArrAmountPaid: number;
  servArrSupAmountPaid: number;
  policyStatus: string;
  companyToPay: string;
  servArrPayeeNumber: string;
  servArrCancelDate: number;
  servArrSupAmountDue: number;
  servArrAmountDue: number;
  servArrCoverage: number;
}
interface ComponentProps {
  hazImpounds: Array<ImpoundPolicyListResponse>;
  compCodes: Set<string>;
  payeeNumbers: Set<string>;
}
const HazardAccruals: React.FC<ComponentProps> = (props) => {
  const { token } = useAuthContext();
  const [servicingArrayEntryImpounds, setServicingArrayEntryImpounds] = useState<{
    [key: string]: ServicingArrayEntryImpound[];
  }>({});
  const [showPaidImpoundLines, setShowPaidImpoundLines] = useState<boolean>(false);
  const [halfDecades, setHalfDecades] = useState<Array<number>>([]);
  const [selectedHalfDecade, setSelectedHalfDecade] = useState<string>('');

  const {
    data: hazPayeeInfo,
    status: hazPayeeStatus,
    error: hazPayeeErrors,
  } = useInsCompInfo(props.compCodes, token, 'hazInsCompInfo');
  const {
    data: hazMiscInfo,
    status: hazMiscStatus,
    error: hazMiscErrors,
  } = useMiscPayeeInfo(props.payeeNumbers, token, 'hazMiscPayeeInfo');

  const flattenedHazImpounds: Array<ImpoundPolicyListResponse> = useMemo(() => {
    return props.hazImpounds.flatMap((impList) => impList);
  }, [props.hazImpounds]);

  useEffect(() => {
    const tempServicingArrayEntryImpounds: Array<ServicingArrayEntryImpound> = [];
    flattenedHazImpounds.forEach((impound) => {
      if (impound.servicingArray.length > 0) {
        impound.servicingArray.forEach((entry) => {
          tempServicingArrayEntryImpounds.push({
            ...impound,
            payAgent: entry.payAgent,
            unitNo: entry.unitNo,
            agentCompany: entry.agentCompany,
            servArrComment: entry.servArrComment,
            policyNo: entry.policyNo,
            servArrAmountPaid: entry.servArrAmountPaid,
            servArrSupAmountPaid: entry.servArrSupAmountPaid,
            policyStatus: entry.policyStatus,
            companyToPay: entry.companyToPay,
            servArrPayeeNumber: entry.servArrPayeeNumber,
            servArrCancelDate: entry.servArrCancelDate,
            servArrSupAmountDue: entry.servArrSupAmountDue,
            servArrAmountDue: entry.servArrAmountDue,
            servArrCoverage: entry.servArrCoverage,
          });
        });
      }
      tempServicingArrayEntryImpounds.forEach((impound) => {
        delete impound.servicingArray;
      });
    });
    tempServicingArrayEntryImpounds.sort((a, b) => {
      return a.renewalDte - b.renewalDte;
    }); //order by payment date
    const indexes: { [key: string]: ServicingArrayEntryImpound[] } = { ['unpaid']: [] };
    let currentHalfDecade: number = 0;
    let currentHalfDecadeFirstIndex: number = 0;
    for (let i = 0; i < tempServicingArrayEntryImpounds.length; i++) {
      const currentServicingArrayEntryImpound: ServicingArrayEntryImpound = tempServicingArrayEntryImpounds[i];
      const year: number = epochToDate(currentServicingArrayEntryImpound.renewalDte).getFullYear();
      const halfDecade: number = Math.floor(year / 5) * 5;
      if (currentHalfDecade === 0) {
        currentHalfDecade = halfDecade;
      }
      if (halfDecade !== currentHalfDecade) {
        indexes[currentHalfDecade.toString()] = tempServicingArrayEntryImpounds.slice(currentHalfDecadeFirstIndex, i);
        if (!halfDecades.includes(currentHalfDecade)) {
          setHalfDecades([...halfDecades, currentHalfDecade]);
        }
        currentHalfDecade = halfDecade;
        currentHalfDecadeFirstIndex = i;
      }
      if (
        currentServicingArrayEntryImpound.servArrAmountDue > currentServicingArrayEntryImpound.servArrAmountPaid ||
        currentServicingArrayEntryImpound.servArrSupAmountDue > currentServicingArrayEntryImpound.servArrSupAmountPaid
      ) {
        indexes['unpaid'] = [...indexes['unpaid'], currentServicingArrayEntryImpound];
      }
    }
    if (tempServicingArrayEntryImpounds.length !== 0) {
      indexes[currentHalfDecade.toString()] = tempServicingArrayEntryImpounds.slice(currentHalfDecadeFirstIndex);
      if (!halfDecades.includes(currentHalfDecade)) {
        setHalfDecades([...halfDecades, currentHalfDecade]);
      }
    }
    setServicingArrayEntryImpounds(indexes);
  }, [flattenedHazImpounds, halfDecades]);

  //sets the selected half decade to the first half-decade upon population of the half-decade array
  useEffect(() => {
    if (halfDecades.length > 0) {
      halfDecades.sort((a, b) => a - b);
      setSelectedHalfDecade(halfDecades[0].toString());
    }
  }, [halfDecades, showPaidImpoundLines]);

  const handlePaidImpoundToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    setShowPaidImpoundLines(value);
  }, []);

  const handleHalfDecadeSelection = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value: string = event.target.value;
    setSelectedHalfDecade(value);
  }, []);

  if (hazPayeeStatus === 'pending' || hazMiscStatus === 'pending') return <div>Loading...</div>;
  if ((hazPayeeErrors ?? []).length > 0 || (hazMiscErrors ?? []).length > 0)
    return (
      <>
        <h2>Encountered errors while obtaining Hazard Insurance Information.</h2>
        <ul>
          {[...(hazPayeeErrors ?? []), ...(hazMiscErrors ?? [])].map((queryError, index) => {
            return <li key={index}>{queryError.message}</li>;
          })}
        </ul>
      </>
    );

  return (
    <>
      <form>
        <span>Show Fully Paid Impound Lines?</span>
        <input
          type='checkbox'
          name='showPaidImpoundLines'
          checked={showPaidImpoundLines}
          onChange={handlePaidImpoundToggle}
        ></input>
        {showPaidImpoundLines && halfDecades.length > 0 && (
          <div>
            <span>Select Half-Decade:</span>
            {halfDecades.map((halfDecade, index) => (
              <label key={index} className={css.radioLabel}>
                <input
                  type='radio'
                  value={halfDecade.toString()}
                  checked={selectedHalfDecade === halfDecade.toString()}
                  onChange={handleHalfDecadeSelection}
                ></input>
                {`${halfDecade.toString()} - ${(halfDecade + 4).toString()}`}
              </label>
            ))}
          </div>
        )}
      </form>
      <table className={css.hazTable}>
        <caption className='h3 semibold'>Hazard&nbsp;Insurance&nbsp;Accruals</caption>
        <thead>
          <tr>
            <th>Impound Type</th>
            <th>Unit No.</th>
            <th>Insurance Company</th>
            <th>Due Date</th>
            <th>Payment Due</th>
            <th>Sup. Payment Due</th>
            <th>Payment Status</th>
            <th>Policy No.</th>
            <th>Agent</th>
            <th>Pay Agent?</th>
            <th>Coverage</th>
            <th>Cancelation Date</th>
            <th>Status</th>
            <th>Comment</th>
          </tr>
        </thead>
        <tbody>
          {Object.values(servicingArrayEntryImpounds).some(
            (impounds) => Array.isArray(impounds) && impounds.length > 0,
          ) &&
            servicingArrayEntryImpounds[!showPaidImpoundLines ? 'unpaid' : selectedHalfDecade].map((impound, index) => {
              const payeeInfo: InsCompResponse = hazPayeeInfo.find((payee) => payee.compCode === impound.companyToPay);
              const miscInfo: MiscPayeeInfoResponse = hazMiscInfo.find(
                (payee) => payee.payeeNumber === impound.servArrPayeeNumber,
              );
              const agentInfo: InsCompResponse = hazPayeeInfo.find((payee) => payee.compCode === impound.agentCompany);
              const paymentStatus: string =
                impound.policyStatus !== '1'
                  ? 'N/A'
                  : impound.servArrAmountDue <= impound.servArrAmountPaid &&
                      impound.servArrSupAmountDue <= impound.servArrSupAmountPaid
                    ? 'FULLY PAID'
                    : impound.servArrAmountPaid > 0 || impound.servArrSupAmountPaid > 0
                      ? 'PARTLY PAID'
                      : 'NOT PAID';
              return (
                <tr key={index}>
                  <td>{impound.impoundType}</td>
                  <td>{impound.unitNo}</td>
                  {payeeInfo && payeeInfo.compCode.length === 9 && isAlphanumeric(payeeInfo.compCode) ? ( //refer to company code info first if it exists, if not check the misc payee number, if netiher has data, return blank
                    <td>
                      {payeeInfo.name1}{' '}
                      {phoneNumberFormatter(payeeInfo.primaryContactPhone, payeeInfo.primaryContactPhoneExtension)}
                    </td>
                  ) : miscInfo ? (
                    <td>
                      {miscInfo.nameOne} {phoneNumberFormatter(miscInfo.phoneNo, '')}
                    </td>
                  ) : (
                    <td></td>
                  )}
                  {impound.renewalDte ? <td>{formatDate(epochToDate(impound.renewalDte))}</td> : <td></td>}
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={impound.servArrAmountDue}
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  </td>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={impound.servArrSupAmountDue}
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  </td>
                  <td>{paymentStatus}</td>
                  <td>{impound.policyNo}</td>
                  {agentInfo ? ( //agents only refer to company codes
                    <td>
                      {agentInfo.name1}{' '}
                      {phoneNumberFormatter(agentInfo.primaryContactPhone, agentInfo.primaryContactPhoneExtension)}
                    </td>
                  ) : (
                    <td></td>
                  )}
                  {impound.payAgent ? <td>YES</td> : <td>NO</td>}
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={impound.servArrCoverage}
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  </td>
                  {impound.servArrCancelDate ? (
                    <td>{formatDate(epochToDate(impound.servArrCancelDate))}</td>
                  ) : (
                    <td></td>
                  )}
                  {impound.policyStatus === '1' ? <td>Escrowed</td> : <td>Not Escrowed</td>}
                  <td>{impound.servArrComment}</td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </>
  );
};

export default HazardAccruals;
